import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

export type UseComboboxOptions<T> = {
  /** Base id: the listbox is `${id}-listbox`, the rows `${id}-option-${index}`. */
  id: string;
  /** Rows currently shown (already filtered by the caller). */
  options: readonly T[];
  onPick: (option: T) => void;
  /** Enter when no row is to be picked (none highlighted, or `shouldPick` refuses it). */
  onEnter?: () => void;
  /** Whether Enter on the highlighted row picks it (default: always). */
  shouldPick?: (option: T) => boolean;
};

/**
 * State and keyboard handling shared by the app's comboboxes (WAI-ARIA combobox with a listbox
 * popup): open/close, the highlighted row, ↑ ↓ Enter Escape, closing on an outside click and
 * keeping the highlighted row in view. Rendering is left to the component (`ComboboxPopup`).
 */
export function useCombobox<T>({
  id,
  options,
  onPick,
  onEnter,
  shouldPick,
}: UseComboboxOptions<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;
  const activeIndex = active < options.length ? active : options.length - 1;

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setActive(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    // scrollIntoView is missing in jsdom
    document.getElementById(`${id}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [id, open, activeIndex]);

  const pick = (option: T) => {
    onPick(option);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive(Math.min(options.length - 1, activeIndex + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.max(0, activeIndex - 1));
    } else if (event.key === 'Enter') {
      const option = open && activeIndex >= 0 ? options[activeIndex] : undefined;
      if (option !== undefined && (shouldPick?.(option) ?? true)) {
        event.preventDefault();
        pick(option);
      } else if (onEnter) {
        event.preventDefault();
        setOpen(false);
        onEnter();
      } else {
        setOpen(false);
      }
    } else if (event.key === 'Escape') {
      close();
    }
  };

  return {
    open,
    setOpen,
    activeIndex,
    setActive,
    containerRef,
    listboxId,
    optionId,
    pick,
    close,
    onKeyDown,
  };
}

export type ComboboxState<T> = ReturnType<typeof useCombobox<T>>;
